import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Button, Checkbox } from '@abw/badger-ui'
import { getConsentAnalytics, getConsentSearch, setConsent } from '../../utils/Cookies'
import { ConsentEssential, ConsentAnalytics, ConsentSearch } from '../../config/Site'
import { Section } from './Contact'

export const Privacy = () =>
  <>
    <Helmet>
      <title>LFH - Privacy Policy</title>
    </Helmet>
    <div className="darkside about">
      <h1 className="mar-t-none mar-b">Privacy policy</h1>
      <Section icon="user" title="Your personal information" className="mar-t-2">
        <p>
          We will never sell or trade any of your personal information which you
          have provided to us via our web site or any other communications channel.
          But there are a number of situations in which we share information with
          other organisations.  Here's a list of them.
        </p>
        <ul>
          <li>
            <b className="yellow">Payment</b>:
            When you place an order on our web site, the payment is handled by the
            Secure Trading gateway.  This captures your name, address, email address,
            payment card details, and the value of your transaction.  These data are
            used to check your identity, your address, and the validity of your
            payment card - via communication with your card issuer and other
            industry-wide databases - prior to the authorisation of the purchase transaction.
          </li>
          <li className="mar-t-2">
            <b className="yellow">Delivery</b>:
            We'll need to provide the delivery carrier (DPD in most cases) with your
            details in order for them to send you tracking updates. This is your name,
            address, telephone number and email address.
          </li>
          <li className="mar-t-2">
            <b className="yellow">Debt collection</b>:
            If you get into a situation where you owe us money and don't pay it in a
            timely fashion, we may refer you to a debt collection agency. They may in
            turn pass this on to their legal advisers and/or officers of the court when
            necessary.
          </li>
          <li className="mar-t-2">
            <b className="yellow">Breach of contract</b>:
            If you commit a serious breach of contract then we may share any and all
            information about you which we hold with our solicitor and any relevant
            law enforcement agencies. We might also provide other hire companies with
            your name, address and contact details.
          </li>
        </ul>
      </Section>
      <Section icon="cookie" title="How we use cookies and local storage" className="mar-t-2">
        <p>
          Cookies are very small text files that are stored on your computer when
          you visit some websites.  We use cookies to help identify your computer
          so we can tailor your user experience, track shopping basket contents
          and remember where you are in the order process.  You can disable any
          cookies already stored on your computer, but these may stop our website
          from functioning properly.
        </p>
        <h4>Cookies which are strictly necessary for operation of our website</h4>
        <p>
          This website will store a cookie in your browser called{' '}
          <code className="yellow">lfh_session</code>.  This allows us to:
        </p>
        <ul>
          <li>Remember what is in your shopping basket</li>
          <li>Remember where you are in the order process</li>
          <li>Remember that you are logged in and that your session is secure.  You need to be logged in to complete an order.</li>
        </ul>
        <p>
          For compliance with the The EU cookie law (ePrivacy Directive), we have to ask
          your permission to store any additional information in your browser that isn't
          strictly necessary for operation of our website.  When you give your consent for
          us to do that, or if you explicitly indicate that you <i>don't</i> want us to do
          that, then we must, somewhat ironically, store a cookie in your browser to save
          that information.  To that effect, this website will store a cookie in your browser
          called <code className="yellow">lfh_consent</code>.
        </p>
        <p>
          This cookie is used to tell us if you have given or witheld consent for use to
          use:
        </p>
        <ul>
          <li>
            Cookies for Google Analytics.
          </li>
          <li>
            Local storage to save your search preferences.
          </li>
        </ul>
        <p>
          These are described in further detail below.
        </p>
        <h4 className="section-subtitle">Cookies for Google Analytics</h4>
        <p>
          If you give consent for us to use Google Analytics then one or more cookies may be
          stored in your browser by the Google Analytics client.
        </p>
        <p>
          This allows us to tell (anonymously) which pages you've been looking at and for how
          long.  This helps us to understand which of our pages and products are more popular
          than others so that we can improve the services that we offer.
        </p>
        <h4 className="section-subtitle">Local storage to save your search parameters</h4>
        <p>
          Local storage is a similar technology to cookies that allows us to store
          some additional information in your web browser. The information is <i>only</i>{' '}
          stored in your web browser and isn't shared with our web server or any third party.
        </p>
        <p>
          If you consent to us using it then we will store your search parameters so that we
          can pre-select your camera type, lens mount and preferred options for ordering search
          results when you return to our web site.
        </p>
        <ul>
          <li><code className="yellow">search-bodyType</code> is used to save your selected Camera type, e.g. Canon, Sony, Nikon, etc.</li>
          <li><code className="yellow">search-lensMount</code> is used to save your lens mount where appropriate, e.g. EF or RF for Canon cameras, F or Z for Nikon cameras.</li>
          <li><code className="yellow">search-sortOrder</code> is used to save the sort order that you select to display results, e.g. Sort by name, price, focal length, etc.</li>
          <li><code className="yellow">search-sortDirection</code> is used to save your choice of sorting the items in ascending or descending order.</li>
          <li><code className="yellow">search-sortNew</code> is used to save your choice of showing new items first or not.</li>
          <li><code className="yellow">recent-products</code> is used to save the 12 products you've most recently looked.</li>
        </ul>
        <p>
          This website will not share any personal information with third parties.
        </p>
      </Section>
      <Section title="Your current consent options">
        <Cookies/>
      </Section>
    </div>
  </>

const Cookies = () => {
  // initial values read from cookie
  const [cookies, setCookies] = useState({
    analytics: getConsentAnalytics(),
    search:    getConsentSearch()
  });
  // interim values changing as user checks/unchecks options
  const [analytics, setAnalytics] = useState(cookies.analytics);
  const [search,    setSearch   ] = useState(cookies.search);
  // save options
  const save = () => {
    const gca = analytics ? ConsentAnalytics : null;
    const gcs = search    ? ConsentSearch    : null;
    setConsent([ ConsentEssential, gca, gcs ]);
    setCookies({ analytics, search });
  }

  return (
    <div>
      <div className="grid-2 gap-1 stack-tablet">
        <Checkbox text="Google Analytics"   value={analytics} onChange={setAnalytics}/>
        <Checkbox text="Search parameters"  value={search}    onChange={setSearch}/>
      </div>
      <div className="text-right">
        { analytics !== cookies.analytics || search !== cookies.search
          ? <Button color="blue" solid text="Save Preferences" iconRight="floppy-disk" onClick={save}/>
          : null
        }
      </div>
    </div>
  )
}

export default Privacy